import CustomTooltip from './CustomTooltip';
import { Badge, FormHelperText, useMediaQuery } from '@mui/material';
import { format, isWithinInterval } from 'date-fns';
import React, { useEffect, useState } from 'react';
import { Control, Controller, useWatch } from 'react-hook-form';
import { DayPicker, PropsRange, PropsBase } from 'react-day-picker';
import 'react-day-picker/style.css';
import { useGetAllPublicHolidaysForYearAndCountryQuery } from '../../app/Slices/PublicHolidaysSlice';
import { FormValues } from '../types/experience';
import UserRole from '../../shared/AuthRoles';
import { useGetProfileDetailsQuery } from '../../app/Slices/SevenDayApiSlice';
import { dayToNumber } from '../../shared/utils';
import { theme } from '../../shared/theme';

const css = `
    .rdp-root {
        --rdp-accent-color: ${theme.palette.textAccent.main};
        --rdp-accent-background-color: ${theme.palette.textAccent.contrastText};
        --rdp-day_button-width: 40px;
        --rdp-day_button-height: 40px;
        --rdp-day-width: 40px;
        --rdp-day-height: 40px;
        --rdp-today-color: ${theme.palette.warning.main};
        font-size: .875rem;
    }

    .rdp-range_middle {
        color: ${theme.palette.textAccent.main};
    }

    .rdp-selected {
        font-size: 14px;
    }

    .rdp-footer {
        font-size: 14px;
    }

    .rdp-months {
        flex-direction: column;
    }

    .rdp-dropdown {
        font-family: 'Inter', sans-serif;
    }

    .rdp-day {
        font-weight: 500;
    }

    .rdp-month thead th {
        font-size: .875rem;
        font-weight: 700;
    }
`;

export type CheckinDetails = {
    checkinFrom?: string;
    checkoutBefore?: string;
};

type CalendarDay = {
    date: string;
    checkinFrom?: string;
    checkoutBefore?: string;
};

interface CalendarProps extends Omit<PropsBase & PropsRange, 'mode'> {
    name: string;
    control: Control<any>;
    experience?: FormValues;
    availability?: {
        blocked: Array<any>;
        limited: Array<CalendarDay>;
    };
    limitedDates?: Record<string, CheckinDetails>;
    isExperiencePage?: boolean;
    setIsWaitingList?: any;
    numberOfMonths?: number;
    isItineraryItemForm?: boolean;
}

const FormCalendar = ({
    name,
    control,
    experience,
    availability,
    limitedDates,
    setIsWaitingList,
    isExperiencePage,
    numberOfMonths,
    isItineraryItemForm,
    ...props
}: CalendarProps) => {
    const loggedInUser = UserRole();
    const isGuestBoatCalendar =
        loggedInUser?.role.includes('guest') && experience?.type === 'boat';
    const isMobile = useMediaQuery('(max-width: 770px)');

    const [modifiers, setModifiers] = useState<any>({});

    const date = useWatch({ control, name });

    // Convert blocked dates to a Set for fast lookups
    const blockedDates: Set<string> = new Set(
        availability?.blocked.flatMap((date: any) => [date.from, date.to]),
    );

    const getDayButtonProps = (
        dateStr: string,
        modifiers: any,
    ): { tooltip: string; dotBadge: boolean } => {
        if (
            minLength &&
            modifiers.selected &&
            !modifiers.range_start &&
            !modifiers.range_end
        ) {
            return { tooltip: minLength, dotBadge: false };
        }

        if (!limitedDates || !limitedDates[dateStr])
            return { tooltip: '', dotBadge: false };

        const details = limitedDates[dateStr];

        if (
            details.checkinFrom &&
            !details.checkoutBefore &&
            highlightCheckinOnly(
                dateStr,
                details.checkinFrom,
                blockedDates,
                experience?.checkinTime,
            )
        ) {
            return {
                tooltip: `Check-in from ${details.checkinFrom}`,
                dotBadge: !modifiers.selected && !modifiers.disabled,
            };
        }

        if (
            details.checkoutBefore &&
            !details.checkinFrom &&
            highlightCheckoutOnly(
                dateStr,
                details.checkoutBefore,
                blockedDates,
                experience?.checkoutTime,
            )
        ) {
            return {
                tooltip: `Checkout before ${details.checkoutBefore}`,
                dotBadge: !modifiers.selected && !modifiers.disabled,
            };
        }

        if (details.checkoutBefore && details.checkinFrom) {
            return {
                tooltip: `Checkout before ${details.checkoutBefore} Check-in from ${details.checkinFrom} `,
                dotBadge: !modifiers.selected && !modifiers.disabled,
            };
        }

        return { tooltip: '', dotBadge: false };
    };

    const highlightCheckinOnly = (
        date: string,
        checkinFrom: string,
        blockedDates: Set<string>,
        experienceCheckin?: string,
    ): boolean => {
        // Do not highlight if checkinFrom is the same as experience checkin time and previous day is blocked
        if (!experienceCheckin) return true;
        const previousDay = new Date(date);
        previousDay.setDate(previousDay.getDate() - 1);
        return (
            !blockedDates.has(format(previousDay, 'yyyy-MM-dd')) ||
            new Date(`${date}T${checkinFrom}`).getTime() >
                new Date(`${date}T${experienceCheckin}`).getTime()
        );
    };

    const highlightCheckoutOnly = (
        date: string,
        checkoutBefore: string,
        blockedDates: Set<string>,
        experienceCheckout?: string,
    ): boolean => {
        // Do not highlight if checkoutBefore is the same as experience checkout Time and next day is blocked
        if (!experienceCheckout) return true;
        const nextDay = new Date(date);
        nextDay.setDate(nextDay.getDate() + 1);
        return (
            !blockedDates.has(format(nextDay, 'yyyy-MM-dd')) ||
            new Date(`${date}T${checkoutBefore}`).getTime() <
                new Date(`${date}T${experienceCheckout}`).getTime()
        );
    };

    const { data: publicHolidaysAustraliaCurrentYear } =
        useGetAllPublicHolidaysForYearAndCountryQuery({
            country: 'AU',
            year: new Date().getFullYear(),
        });
    const { data: publicHolidaysAustraliaNextYear } =
        useGetAllPublicHolidaysForYearAndCountryQuery({
            country: 'AU',
            year: new Date().getFullYear() + 1,
        });
    const { data: publicHolidaysAustraliaYearAfterNextYear } =
        useGetAllPublicHolidaysForYearAndCountryQuery({
            country: 'AU',
            year: new Date().getFullYear() + 2,
        });

    const publicHolidaysLoaded =
        publicHolidaysAustraliaCurrentYear &&
        publicHolidaysAustraliaNextYear &&
        publicHolidaysAustraliaYearAfterNextYear;

    const publicHolidaysAustralia =
        publicHolidaysAustraliaCurrentYear &&
        publicHolidaysAustraliaNextYear &&
        publicHolidaysAustraliaYearAfterNextYear
            ? [
                  ...publicHolidaysAustraliaCurrentYear,
                  ...publicHolidaysAustraliaNextYear,
                  ...publicHolidaysAustraliaYearAfterNextYear,
              ]
            : [];

    const filteredPublicHolidaysAustralia = publicHolidaysAustralia
        ?.filter((data) => {
            return (
                data.global || data.counties.includes(`AU-${experience?.state}`)
            );
        })
        .map((data) => new Date(data.date));

    const { data: profileDetails } = useGetProfileDetailsQuery('/profile');
    const beckFamily = profileDetails?.companyName
        ?.toLowerCase()
        .includes('beck');

    const availableDays = {
        sunday: experience?.sunday,
        monday: experience?.monday,
        tuesday: experience?.tuesday,
        wednesday: experience?.wednesday,
        thursday: experience?.thursday,
        friday: experience?.friday,
        saturday: experience?.saturday,
    };

    const dayOfWeek = Object.entries(availableDays)
        .filter(([, available]) => !available)
        .map(([day]) => dayToNumber[day]);

    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    tomorrow.setHours(0, 0, 0, 0);

    const guestBoatMaxDate = new Date(tomorrow);
    guestBoatMaxDate.setMonth(guestBoatMaxDate.getMonth() + 9);
    guestBoatMaxDate.setHours(23, 59, 59, 999);

    const getCheckinDates = () => {
        if (!availability || !experience) return;

        const checkoutOnly = availability.limited
            .filter((d: CalendarDay) => d.checkoutBefore && !d.checkinFrom)
            .map((d: CalendarDay) => new Date(d.date));

        const checkinOnly = availability.limited
            .filter((d: CalendarDay) => d.checkinFrom)
            .map((d: CalendarDay) => new Date(d.date));

        setModifiers({
            limited: [...checkinOnly, ...checkoutOnly],
            unavailable: [...availability.blocked],
            disabled: [
                {
                    before: new Date(
                        new Date().getTime() + 24 * 60 * 60 * 1000,
                    ),
                },
                ...(isGuestBoatCalendar ? [{ after: guestBoatMaxDate }] : []),
                ...(!isExperiencePage || loggedInUser?.role.includes('owner')
                    ? availability?.blocked
                    : []),
                ...(loggedInUser?.role.includes('owner') || beckFamily
                    ? []
                    : experience.publicHolidays
                      ? []
                      : filteredPublicHolidaysAustralia),
                !(loggedInUser?.role.includes('owner') || beckFamily) && {
                    dayOfWeek,
                },
            ],
        });
    };

    const getCheckoutDates = () => {
        if (!availability || !experience) return;

        const checkoutOnly = availability.limited
            .filter((d: CalendarDay) => d.checkoutBefore)
            .map((d: CalendarDay) => new Date(d.date));

        const checkinOnly = availability.limited
            .filter(
                (d: CalendarDay) =>
                    d.checkinFrom &&
                    !d.checkoutBefore &&
                    // exclude current day to allow for single day bookings
                    new Date(new Date(d.date).setHours(0)).getTime() !==
                        date?.from.getTime(),
            )
            .map((d: CalendarDay) => new Date(d.date));

        const nextCheckout = checkoutOnly.find(
            (checkout) => new Date(checkout.setHours(0)) > date?.from,
        );

        const prevCheckout = checkoutOnly
            .filter((checkout) => new Date(checkout.setHours(0)) <= date?.from)
            .sort((a, b) => b.getTime() - a.getTime())
            .shift();

        const maxHire = new Date(date?.from);

        if (experience.maxHireHours > 24)
            maxHire.setHours(maxHire.getHours() + experience.maxHireHours - 24);

        const maxHireBefore = new Date(date?.from);
        if (experience.maxHireHours > 24)
            maxHireBefore.setHours(
                maxHireBefore.getHours() - experience.maxHireHours + 24,
            );

        const blockAfter =
            loggedInUser?.role.includes('owner') || beckFamily
                ? nextCheckout
                : nextCheckout && maxHire > nextCheckout
                  ? nextCheckout
                  : maxHire;

        const blockBefore =
            loggedInUser?.role.includes('owner') || beckFamily
                ? new Date()
                : date?.from && !date?.to
                  ? maxHireBefore
                  : new Date();

        const disableNext =
            nextCheckout &&
            nextCheckout.getDate() !== date?.from.getDate() &&
            (nextCheckout?.getTime() - date?.from.getTime()) / 36e5 <
                experience.maxHireHours &&
            new Date(nextCheckout.setDate(nextCheckout.getDate() + 1));

        const disablePrev =
            prevCheckout &&
            new Date(prevCheckout.setDate(prevCheckout.getDate() - 1));

        setModifiers({
            limited: checkoutOnly,
            unavailable: [
                ...availability.blocked,
                disableNext,
                disablePrev,
                ...checkinOnly,
            ],
            disabled: [
                {
                    before: new Date(
                        new Date().getTime() + 24 * 60 * 60 * 1000,
                    ),
                },
                ...(isGuestBoatCalendar ? [{ after: guestBoatMaxDate }] : []),
                {
                    before: blockBefore,
                    after: !date?.to && blockAfter,
                },

                ...(!isExperiencePage || loggedInUser?.role.includes('owner')
                    ? availability.blocked
                    : []),
                ...(loggedInUser?.role.includes('owner') ? checkinOnly : []),
                ...(loggedInUser?.role.includes('owner') || beckFamily
                    ? []
                    : experience.publicHolidays
                      ? []
                      : filteredPublicHolidaysAustralia),
                !(loggedInUser?.role.includes('owner') || beckFamily) && {
                    dayOfWeek,
                },
            ],
        });
    };

    useEffect(() => {
        if (!availability || !publicHolidaysLoaded) return;
        getCheckinDates();

        if (date?.from) getCheckoutDates();
    }, [availability, date, publicHolidaysLoaded]);

    const min = !(loggedInUser?.role.includes('owner') || beckFamily)
        ? experience && (experience.minHireHours - 24) / 24
        : undefined;

    const minLength =
        experience &&
        `${
            experience?.minHireHours < 24
                ? `${experience?.minHireHours} hours`
                : experience.type === 'commercial'
                  ? `${experience?.minHireHours / 24} day`
                  : experience?.minHireHours === 24
                    ? `${experience?.minHireHours / 24} day`
                    : experience?.minHireHours === 48
                      ? `${(experience?.minHireHours - 24) / 24} night`
                      : experience?.minHireHours > 24
                        ? `${(experience?.minHireHours - 24) / 24} night`
                        : `${experience?.minHireHours} hour`
        } minimum`;

    const max = !(loggedInUser?.role.includes('owner') || beckFamily)
        ? experience && experience.maxHireHours > 24
            ? (experience.maxHireHours - 24) / 24
            : 0.99
        : undefined;

    useEffect(() => {
        if (isExperiencePage) {
            setIsWaitingList(false);
        }
        let isUnavailable = false;

        if ((date?.from || date?.to) && modifiers?.unavailable) {
            const checkIfUnavailable = (targetDate: Date) => {
                const targetDateStr = format(targetDate, 'yyyy-MM-dd');

                return modifiers.unavailable.some((item: any) => {
                    if (typeof item === 'string') {
                        return item.startsWith(targetDateStr);
                    }

                    if (typeof item === 'object' && item?.from) {
                        const itemFromStr = format(
                            new Date(item.from),
                            'yyyy-MM-dd',
                        );
                        const itemToStr = item.to
                            ? format(new Date(item.to), 'yyyy-MM-dd')
                            : itemFromStr;

                        return (
                            targetDateStr >= itemFromStr &&
                            targetDateStr <= itemToStr
                        );
                    }

                    return false;
                });
            };

            isUnavailable =
                (date.from && checkIfUnavailable(date.from)) ||
                (date.to && checkIfUnavailable(date.to));

            if (isUnavailable && isExperiencePage) setIsWaitingList(true);
        }

        if ((date?.from || date?.to) && availability) {
            const checkoutOnly = availability.limited
                .filter((d: CalendarDay) => d.checkoutBefore && !d.checkinFrom)
                .map(
                    (d: CalendarDay) => new Date(new Date(d.date).setHours(0)),
                );

            const checkinOnly = availability.limited
                .filter((d: CalendarDay) => d.checkinFrom)
                .map(
                    (d: CalendarDay) => new Date(new Date(d.date).setHours(0)),
                );

            const isIncluded =
                (checkoutOnly.some(
                    (d) =>
                        d.getTime() ===
                        new Date(new Date(date.from).setHours(0)).getTime(),
                ) &&
                    new Date(new Date(date.from).setHours(0)).getTime() !==
                        new Date(new Date(date.to).setHours(0)).getTime()) ||
                (checkinOnly.some(
                    (d) =>
                        d.getTime() ===
                        new Date(new Date(date.to).setHours(0)).getTime(),
                ) &&
                    new Date(new Date(date.from).setHours(0)).getTime() !==
                        new Date(new Date(date.to).setHours(0)).getTime()) ||
                (checkoutOnly.some(
                    (d) =>
                        d.getTime() ===
                        new Date(new Date(date.from).setHours(0)).getTime(),
                ) &&
                    experience &&
                    experience?.minHireHours > 24) ||
                (checkinOnly.some(
                    (d) =>
                        d.getTime() ===
                        new Date(new Date(date.to).setHours(0)).getTime(),
                ) &&
                    experience &&
                    experience?.minHireHours > 24);

            if (isIncluded && isExperiencePage) {
                setIsWaitingList(true);
            }
        }

        if (date?.from && date?.to && modifiers) {
            const from = new Date(date.from);
            const to = new Date(date.to);

            const interval = { start: from, end: to };

            const found = [
                ...(modifiers.limited || []),
                ...(modifiers.unavailable || []),
            ].some((item) => {
                if (!item) return false;

                if (typeof item === 'string') {
                    const itemDate = new Date(item);
                    return isWithinInterval(itemDate, interval);
                }

                if (typeof item === 'object' && item.from) {
                    const itemFrom = new Date(item.from);
                    const itemTo = item.to ? new Date(item.to) : itemFrom;
                    return (
                        isWithinInterval(itemFrom, interval) ||
                        isWithinInterval(itemTo, interval) ||
                        (itemFrom <= from && itemTo >= to)
                    );
                }
                if (typeof item === 'object') {
                    return isWithinInterval(item, interval);
                }

                return false;
            });

            if (found && isExperiencePage) {
                setIsWaitingList(true);
            }
        }
    }, [modifiers, date]);

    return (
        <>
            <style>{css}</style>
            <Controller
                name={name}
                control={control}
                render={({ field: { value }, fieldState: { error } }) => (
                    <>
                        <DayPicker
                            {...props}
                            mode='range'
                            numberOfMonths={numberOfMonths}
                            selected={value}
                            modifiers={modifiers}
                            styles={
                                isItineraryItemForm && !isMobile
                                    ? {
                                          months: {
                                              display: 'flex',
                                              flexDirection: 'row',
                                          },
                                      }
                                    : {}
                            }
                            modifiersStyles={{
                                disabled: {
                                    color: `${theme.palette.custom.bodyTextColor}`,
                                },
                                today: { color: '' },
                            }}
                            min={min}
                            max={max}
                            disabled={modifiers.disabled}
                            excludeDisabled
                            components={{
                                DayButton: (props: {
                                    [x: string]: any;
                                    day: any;
                                    modifiers: any;
                                }) => {
                                    const { day, modifiers, ...buttonProps } =
                                        props;

                                    const dateStr = format(
                                        day.date,
                                        'yyyy-MM-dd',
                                    );

                                    const { tooltip, dotBadge } =
                                        getDayButtonProps(dateStr, modifiers);
                                    const isGuestBoatFutureLimitedDate =
                                        isGuestBoatCalendar &&
                                        day.date.getTime() >
                                            guestBoatMaxDate.getTime();
                                    const isNotCateredWeekday =
                                        !(
                                            loggedInUser?.role.includes(
                                                'owner',
                                            ) || beckFamily
                                        ) &&
                                        dayOfWeek.includes(day.date.getDay());

                                    return (
                                        <>
                                            <Badge
                                                variant={
                                                    dotBadge
                                                        ? 'dot'
                                                        : 'standard'
                                                }
                                                overlap='circular'
                                                sx={{
                                                    '.MuiBadge-badge': {
                                                        bgcolor: 'text.primary',
                                                        minWidth: 4,
                                                        height: 4,
                                                    },
                                                }}
                                            >
                                                <CustomTooltip title={tooltip}>
                                                    <button
                                                        {...buttonProps}
                                                        style={{
                                                            ...(modifiers.disabled &&
                                                                (!isGuestBoatFutureLimitedDate ||
                                                                    modifiers.unavailable ||
                                                                    isNotCateredWeekday) && {
                                                                    textDecoration:
                                                                        'line-through',
                                                                }),
                                                            ...(modifiers.unavailable && {
                                                                color: theme
                                                                    .palette
                                                                    .lightGrey
                                                                    .main,
                                                            }),
                                                        }}
                                                    />
                                                </CustomTooltip>
                                            </Badge>
                                        </>
                                    );
                                },
                            }}
                        />

                        {!!error && (
                            <FormHelperText error>
                                {error.message}
                            </FormHelperText>
                        )}
                    </>
                )}
            />
        </>
    );
};

export default FormCalendar;
